import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, formatErr } from "../lib/auth";
import { BACKEND_URL } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { AutoGlowBrand } from "./components/AutoGlowLogo";
import ThemeSwitch from "./components/ThemeSwitch";
import GlobalHeader from "../components/layout/GlobalHeader";
import PasswordInput from "./components/PasswordInput";
import { toast } from "sonner";
import { ArrowRight, CheckCircle, Sparkle, Buildings, UserPlus } from "@phosphor-icons/react";

const pub = axios.create({ baseURL: `${BACKEND_URL}/api` });

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const urlInvite = (params.get("invite") || "").toUpperCase();

  const [mode, setMode] = useState(urlInvite ? "colaborador" : "titular");
  const [form, setForm] = useState({
    org_name: "", nif: "", name: "", email: "", password: "",
    invite_code: urlInvite,
  });
  const [inviteMeta, setInviteMeta] = useState(null); // { valid, org_name, org_logo_url, ... }
  const [busy, setBusy] = useState(false);

  // Resolve invite from URL on mount (and whenever code changes)
  useEffect(() => {
    const code = (form.invite_code || "").trim().toUpperCase();
    if (!code) { setInviteMeta(null); return; }
    if (code.length !== 8) {
      setInviteMeta({ valid: false, reason: "invalid_length" });
      return;
    }
    pub.get(`/marketplace/invites/${code}`).then((r) => setInviteMeta(r.data)).catch(() => setInviteMeta(null));
  }, [form.invite_code]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = mode === "colaborador"
        ? {
            name: form.name, email: form.email, password: form.password,
            invite_code: (form.invite_code || "").trim().toUpperCase(),
          }
        : {
            name: form.name, email: form.email, password: form.password,
            org_name: form.org_name, nif: form.nif,
            // Sprint S-7 · enviar código de afiliado si está en localStorage
            referrer_code: (typeof window !== "undefined" && (localStorage.getItem("ag_ref") || "").toUpperCase()) || "",
          };
      await register(payload);
      toast.success(mode === "colaborador"
        ? "¡Bienvenido al equipo!"
        : "¡Centro creado! 30 días gratis activados.");
      // Fase 2B · B1 · Nuevo titular → arranca First Run directamente
      if (mode !== "colaborador") {
        navigate("/titular/first-run", { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen ag-radial flex flex-col">
      <GlobalHeader variant="auto" testId="register-header" showRegisterCta={false} />
      <div className="flex-1 flex items-start sm:items-center justify-center p-4 sm:p-6 lg:p-10">
      <div className="w-full max-w-5xl grid lg:grid-cols-5 gap-6 lg:gap-8">
        {/* Left: pitch */}
        <div className="lg:col-span-2 lg:pr-4">
          <Link to="/" className="hidden lg:flex items-center gap-2" data-testid="register-logo-link">
            <AutoGlowBrand size={26} />
          </Link>
          <div className="mt-2 lg:mt-8 inline-flex items-center gap-2 px-3 py-1.5 border border-[var(--ag-gold)]/30 rounded-full text-[10px] uppercase tracking-[0.2em] text-[var(--ag-gold)]">
            <Sparkle size={12} weight="fill" /> 30 días gratis · sin tarjeta
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mt-4 lg:mt-5 leading-[1.05] text-[var(--ag-text)]">
            {mode === "colaborador" ? (
              inviteMeta?.valid ? (
                <>Únete a<br/><span className="ag-glow-text">{inviteMeta.org_name}</span>.</>
              ) : (<>Únete a tu<br/><span className="ag-glow-text">equipo</span>.</>)
            ) : (
              <>Crea tu centro<br/>en <span className="ag-glow-text">90 segundos</span>.</>
            )}
          </h1>
          <ul className="mt-6 space-y-2 text-sm text-[var(--ag-text-dim)]">
            {(mode === "colaborador" ? [
              "Accede al panel del centro y a tus servicios",
              "Reparto de comisión configurable con tu retención IRPF",
              "Notificaciones de avisos y disponibilidad",
            ] : [
              "Modo Solo Titular o con equipo — se adapta automáticamente",
              "Tu mini-web pública con reservas online",
              "VAT/IVA · IRPF · Hucha de impuestos automatizados",
              "Pasados 30 días: 29 €/mes · cancela cuando quieras",
            ]).map((t) => (
              <li key={t} className="flex items-start gap-2">
                <CheckCircle size={14} weight="fill" className="text-[var(--ag-aqua-soft)] shrink-0 mt-0.5" /> {t}
              </li>
            ))}
          </ul>
        </div>

        {/* Right: form */}
        <form onSubmit={submit} className="lg:col-span-3 ag-card p-6 sm:p-8" data-testid="register-form">
          {/* Mode switcher */}
          <div className="grid grid-cols-2 gap-2 p-1 bg-[var(--ag-midnight)] rounded-md border border-[var(--ag-border)]">
            <button type="button" onClick={()=>setMode("titular")}
              className={`px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded transition-colors flex items-center justify-center gap-1.5 ${mode==="titular"?"bg-[var(--ag-gold)] text-[var(--ag-midnight)]":"text-[var(--ag-text-dim)] hover:text-white"}`}
              data-testid="reg-mode-titular">
              <Buildings size={14} weight="fill" /> Soy titular
            </button>
            <button type="button" onClick={()=>setMode("colaborador")}
              className={`px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded transition-colors flex items-center justify-center gap-1.5 ${mode==="colaborador"?"bg-[var(--ag-aqua)] text-white":"text-[var(--ag-text-dim)] hover:text-white"}`}
              data-testid="reg-mode-colab">
              <UserPlus size={14} weight="fill" /> Tengo invitación
            </button>
          </div>

          {/* Invite resolution banner */}
          {mode === "colaborador" && form.invite_code && inviteMeta && (
            <div className={`mt-4 p-3 rounded border flex items-center gap-3 ${inviteMeta.valid ? "border-[var(--ag-gold)]/40 bg-[var(--ag-gold)]/5" : "border-red-500/40 bg-red-500/5"}`} data-testid="invite-banner">
              {inviteMeta.valid ? (
                <>
                  {inviteMeta.org_logo_url ? (
                    <img src={inviteMeta.org_logo_url} alt="" className="w-9 h-9 rounded bg-white object-contain p-0.5" />
                  ) : (
                    <div className="w-9 h-9 rounded bg-[var(--ag-gold)] text-[var(--ag-midnight)] grid place-items-center font-display font-black">
                      {inviteMeta.org_name?.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0 text-sm">
                    <div className="text-[10px] uppercase tracking-wider text-[var(--ag-gold)]">Invitación válida</div>
                    <div className="font-display font-bold text-[var(--ag-text)] truncate">{inviteMeta.org_name}</div>
                    {inviteMeta.org_city && <div className="text-[11px] text-[var(--ag-text-dim)]">{inviteMeta.org_city}</div>}
                  </div>
                </>
              ) : (
                <div className="text-sm text-red-300">
                  <b>Código inválido o ya utilizado.</b> Pide uno nuevo al titular.
                </div>
              )}
            </div>
          )}

          <h2 className="font-display font-bold text-2xl tracking-tighter mt-5 text-[var(--ag-text)]">
            {mode === "titular" ? "Datos del centro" : "Únete al equipo"}
          </h2>
          <p className="text-sm text-[var(--ag-text-dim)] mt-1">
            {mode === "titular"
              ? "Información mínima para empezar — todo es editable después."
              : "Introduce el código que te ha facilitado el titular del centro (o usa el enlace de invitación)."}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
            {mode === "titular" ? (
              <>
                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">Nombre del centro *</Label>
                  <Input value={form.org_name} onChange={upd("org_name")} required className="mt-1" placeholder="Detail Pro, SL" data-testid="reg-org-name" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">NIF / CIF / Tax ID</Label>
                  <Input value={form.nif} onChange={upd("nif")} className="mt-1 font-mono-tab" placeholder="B12345678" data-testid="reg-nif" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">Tu nombre *</Label>
                  <Input value={form.name} onChange={upd("name")} required className="mt-1" placeholder="Carlos Pérez" data-testid="reg-name" />
                </div>
              </>
            ) : (
              <>
                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">Código de invitación *</Label>
                  <Input
                    value={form.invite_code}
                    onChange={(e)=>setForm({...form, invite_code: e.target.value.toUpperCase()})}
                    required
                    className="mt-1 font-mono-tab tracking-[0.3em] text-center text-lg uppercase"
                    placeholder="XXXXXXXX"
                    maxLength={8}
                    data-testid="reg-invite-code"
                  />
                  <p className="text-[11px] text-[var(--ag-text-dim)] mt-1.5">Lo recibes del titular del centro · 8 caracteres</p>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">Tu nombre *</Label>
                  <Input value={form.name} onChange={upd("name")} required className="mt-1" placeholder="María García" data-testid="reg-name" />
                </div>
              </>
            )}
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">Email *</Label>
              <Input type="email" value={form.email} onChange={upd("email")} required className="mt-1" placeholder="tu@email.es" data-testid="reg-email" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">Contraseña *</Label>
              <PasswordInput value={form.password} onChange={upd("password")} required minLength={8} className="mt-1" placeholder="Mín. 8 caracteres" testId="reg-password" />
            </div>
          </div>

          <Button type="submit" disabled={busy || (mode === "colaborador" && inviteMeta && !inviteMeta.valid)} className="w-full mt-6 ag-btn-gold h-12 text-base" data-testid="register-submit">
            {busy
              ? "Creando…"
              : (
                <span className="flex items-center justify-center gap-2">
                  {mode === "titular" ? "Probar AutoGlow gratis · 30 días" : "Unirme al equipo"} <ArrowRight weight="bold" />
                </span>
              )}
          </Button>
          <p className="text-xs text-[var(--ag-text-dim)] mt-4 text-center">
            ¿Ya tienes cuenta? <Link to="/login" className="text-[var(--ag-gold)] underline" data-testid="goto-login">Inicia sesión</Link>
          </p>
        </form>
      </div>
      </div>
    </div>
  );
}
